import symbolGold from '@powerful/brand/assets/logo/powerful-connect-symbol-gold.svg';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import { Icon, type IconName } from '../icons/icon';
import type { NavItem } from './nav-items';

/** Ícone de cada separador do consumidor — o mesmo nos tabs e na sidebar (canvas `NAV`). */
export const CONSUMER_NAV_ICONS: Record<string, IconName> = {
  home: 'home',
  map: 'map',
  bookings: 'calendar',
  favorites: 'heart',
  profile: 'user',
};

export interface ConsumerSidebarProps {
  itens: readonly NavItem[];
  label: string;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * Sidebar do consumidor a partir de 1024 px — refino **R12** do canvas T2.1.
 *
 * Colapsada por omissão (72 px, só ícones): a descoberta precisa da largura para o painel de
 * filtros e o feed. Expandida mede `--sidebar` (244 px). Colapsada, o rótulo continua no DOM
 * (visualmente oculto), por isso cada link mantém o nome acessível — não depende de `title`.
 * A troca com os bottom tabs é por CSS (`.pc-cside` / `lg:hidden`), sem salto no primeiro paint.
 */
export function ConsumerSidebar({ itens, label, expanded, onToggle }: ConsumerSidebarProps) {
  const { t } = useTranslation();

  return (
    <nav className="pc-cside" aria-label={label} data-testid="consumer-sidebar">
      <div className="pc-cside-in">
        <Link className="side-brand hit" to="/home" aria-label={t('discovery.brandHome')}>
          <img src={symbolGold} alt="" width={30} height={30} />
          <span className="pc-brand-name pc-cside-lbl" style={{ display: 'inline' }}>Powerful Connect</span>
        </Link>
        <button
          className="hit pc-cside-toggle"
          type="button"
          aria-expanded={expanded}
          aria-controls="cside-nav"
          aria-label={expanded ? t('nav.sidebar.collapse') : t('nav.sidebar.expand')}
          onClick={onToggle}
        >
          <Icon name="panel" />
        </button>
        <ul className="side-nav" id="cside-nav">
          {itens.map((item) => (
            <li key={item.id}>
              <NavLink to={item.to} end={item.to === itens[0]?.to} title={expanded ? undefined : t(item.labelKey)}>
                <Icon name={CONSUMER_NAV_ICONS[item.id] ?? 'grid'} />
                <span className="pc-cside-lbl">{t(item.labelKey)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
